import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Scissors, MapPin, Clock, Save, Upload, X, 
  Phone, Mail, Globe, Loader2, Star, QrCode, Download, Share2, Copy, ExternalLink
} from 'lucide-react';
import api from '../utils/api';
import LogoUpload from '../components/LogoUpload';
import WorkingHoursModal from '../components/WorkingHoursModal';
import AddressModal from '../components/AddressModal';
import { LoadingState, PageHeader } from '../components/ui';
import { useToast } from '../components/ToastProvider';

export default function Settings() {
  const { id } = useParams();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [barbershop, setBarbershop] = useState(null);
  const [publicQr, setPublicQr] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [qrMessage, setQrMessage] = useState('');
  const [formData, setFormData] = useState({
    nome: '',
    reviews_enabled: true,
    cancellation_policy_hours: 2
  });
  const [showHoursModal, setShowHoursModal] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);

  const loadBarbershop = async () => {
    try {
      const response = await api.get(`/barbershops/${id}`);
      setBarbershop(response.data);
      setFormData({
        nome: response.data.nome || '',
        reviews_enabled: response.data.reviews_enabled !== false,
        cancellation_policy_hours: response.data.cancellation_policy_hours ?? 2
      });
    } catch (error) {
      console.error('Erro ao carregar barbearia:', error);
    }
  };

  const loadPublicQr = async () => {
    setQrLoading(true);
    try {
      const response = await api.get(`/barbershops/${id}/qrcode`);
      setPublicQr(response.data);
    } catch (error) {
      console.error('Erro ao carregar QR Code:', error);
    } finally {
      setQrLoading(false);
    }
  };

  useEffect(() => {
    loadBarbershop();
    loadPublicQr();
  }, [id]);

  const copyPublicLink = async () => {
    if (!publicQr?.publicUrl) return;
    await navigator.clipboard.writeText(publicQr.publicUrl);
    setQrMessage('Link copiado.');
    setTimeout(() => setQrMessage(''), 2500);
  };

  const sharePublicLink = async () => {
    if (!publicQr?.publicUrl) return;
    if (navigator.share) {
      await navigator.share({
        title: barbershop?.nome || 'BarberFlow',
        text: 'Agende seu horário pela nossa página.',
        url: publicQr.publicUrl
      });
      return;
    }
    await copyPublicLink();
  };

  const downloadPublicQr = async () => {
    try {
      const response = await api.get(`/barbershops/${id}/qrcode/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${publicQr?.barbershop?.slug || 'barberflow'}-qrcode.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao baixar QR Code');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await api.put(`/barbershops/${id}`, formData);
      await loadBarbershop();
      toast.success('Configuracoes salvas.');
    } catch (error) {
      console.error('Erro ao salvar:', error);
      toast.error('Erro ao salvar configuracoes');
    } finally {
      setLoading(false);
    }
  };

  if (!barbershop) { return <LoadingState label="Carregando configuracoes..." />; }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <PageHeader
        eyebrow="Configuracoes"
        title="Configuracoes da barbearia"
        description="Gerencie identidade, pagina publica, horarios, contato, avaliacoes e politicas."
        icon={Scissors}
        action={
          <LogoUpload
            barbershopId={id}
            currentLogo={barbershop.logo_url}
            onLogoChange={(logoUrl) => {
              setBarbershop(prev => ({ ...prev, logo_url: logoUrl }));
              loadBarbershop();
            }}
          />
        }
      />
      <div className="card overflow-hidden border-primary-500/20">
        <div className="p-6 border-b border-dark-600 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-primary-500/10 border border-primary-500/20 flex items-center justify-center">
              <QrCode className="w-5 h-5 text-primary-300" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Pagina publica e QR Code</h2>
              <p className="text-sm text-gray-400">Compartilhe o link da barbearia para clientes agendarem direto.</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={copyPublicLink} disabled={!publicQr?.publicUrl} className="btn-secondary py-2 px-3">
              <Copy className="w-4 h-4 mr-2" />
              Copiar link
            </button>
            <button onClick={sharePublicLink} disabled={!publicQr?.publicUrl} className="btn-secondary py-2 px-3">
              <Share2 className="w-4 h-4 mr-2" />
              Encaminhar
            </button>
            <button onClick={downloadPublicQr} disabled={!publicQr?.qrCode} className="btn-primary py-2 px-3">
              <Download className="w-4 h-4 mr-2" />
              Baixar QR
            </button>
          </div>
        </div>

        <div className="p-6 grid lg:grid-cols-[160px_1fr] gap-5 items-center">
          <div className="w-40 h-40 rounded-2xl bg-white p-3 flex items-center justify-center">
            {qrLoading ? (
              <span className="text-dark-700 text-sm">Gerando...</span>
            ) : publicQr?.qrCode ? (
              <img src={publicQr.qrCode} alt="QR Code da pagina publica" className="w-full h-full object-contain" />
            ) : (
              <QrCode className="w-16 h-16 text-dark-500" />
            )}
          </div>

          <div className="space-y-3 min-w-0">
            <div>
              <p className="text-xs uppercase tracking-wide text-gray-500">Link publico</p>
              <div className="mt-1 rounded-xl border border-dark-600 bg-dark-700/60 px-4 py-3 flex items-center justify-between gap-3">
                <p className="text-sm text-gray-200 truncate">{publicQr?.publicUrl || 'Carregando link...'}</p>
                {publicQr?.publicUrl && (
                  <a href={publicQr.publicUrl} target="_blank" rel="noreferrer" className="text-primary-300 hover:text-primary-200">
                    <ExternalLink className="w-4 h-4" />
                  </a>
                )}
              </div>
            </div>
            {qrMessage && <p className="text-sm text-green-400">{qrMessage}</p>}
            <p className="text-xs text-gray-500">
              Use esse QR em artes, recepcao, cartao, Instagram ou envie pelo WhatsApp para o cliente abrir a pagina de agendamento.
            </p>
          </div>
        </div>
      </div>
      {/* Informações Básicas */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Scissors className="w-5 h-5 text-primary-400" />
          Informações Básicas
        </h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Nome da Barbearia
            </label>
            <input
              type="text"
              value={formData.nome}
              onChange={(e) => setFormData({...formData, nome: e.target.value})}
              className="input"
              placeholder="Digite o nome da barbearia"
              required
            />
          </div>

          <div className="flex justify-end pt-4">
            <button
              type="submit"
              disabled={loading}
              className="btn-primary px-6 py-2 bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Salvar Alterações
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      <div className="card p-6">
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Star className="w-5 h-5 text-yellow-400" />
          Avaliacoes publicas
        </h2>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <p className="font-medium text-white">Permitir avaliações na página de agendamento</p>
            <p className="text-sm text-gray-400 mt-1">
              Quando desligado, a seção de avaliações e o botão Avaliar somem da página pública.
            </p>
          </div>

          <button
            type="button"
            onClick={() => setFormData(prev => ({ ...prev, reviews_enabled: !prev.reviews_enabled }))}
            className={[
              'relative inline-flex h-7 w-12 flex-shrink-0 items-center rounded-full transition-colors',
              formData.reviews_enabled ? 'bg-primary-600' : 'bg-gray-700'
            ].join(' ')}
            aria-pressed={formData.reviews_enabled}
          >
            <span
              className={[
                'inline-block h-5 w-5 transform rounded-full bg-white transition-transform',
                formData.reviews_enabled ? 'translate-x-6' : 'translate-x-1'
              ].join(' ')}
            />
          </button>
        </div>

        <div className="flex justify-end pt-5">
          <button
            type="button"
            disabled={loading}
            onClick={handleSubmit}
            className="btn-primary px-6 py-2 bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Salvar avaliações'}
          </button>
        </div>
      </div>

      {/* Ações Rápidas */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Clock className="w-5 h-5 text-primary-400" />
          Política de cancelamento e remarcação
        </h2>
        <div className="grid sm:grid-cols-[1fr_180px] gap-4 items-end">
          <div>
            <p className="text-sm text-gray-400">
              Define até quantas horas antes o cliente pode cancelar ou remarcar pelo link público.
            </p>
          </div>
          <div>
            <label className="label">Horas antes</label>
            <input
              type="number"
              min="0"
              max="168"
              className="input"
              value={formData.cancellation_policy_hours}
              onChange={(e) => setFormData({ ...formData, cancellation_policy_hours: Number(e.target.value) })}
            />
          </div>
        </div>
        <div className="flex justify-end pt-5">
          <button type="button" disabled={loading} onClick={handleSubmit} className="btn-primary px-6 py-2">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Salvar política'}
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <button
          onClick={() => setShowAddressModal(true)}
          className="card card-hover p-6 text-left"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
              <MapPin className="w-6 h-6 text-purple-400" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-white">Endereço e contato</h3>
              <div className="space-y-1 mt-1">
                <p className="text-sm text-gray-400">
                  {barbershop.endereco || 'Clique para configurar o endereço'}
                </p>
                {barbershop.telefone && (
                  <p className="text-xs text-gray-500 flex items-center gap-1">
                    <Phone className="w-3 h-3" />
                    {barbershop.telefone}
                  </p>
                )}
                {barbershop.email && (
                  <p className="text-xs text-gray-500 flex items-center gap-1">
                    <Mail className="w-3 h-3" />
                    {barbershop.email}
                  </p>
                )}
                {barbershop.website && (
                  <p className="text-xs text-gray-500 flex items-center gap-1">
                    <Globe className="w-3 h-3" />
                    {barbershop.website}
                  </p>
                )}
              </div>
            </div>
          </div>
        </button>

        <button
          onClick={() => setShowHoursModal(true)}
          className="card card-hover p-6 text-left"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
              <Clock className="w-6 h-6 text-blue-400" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-white">Horários de Funcionamento</h3>
              <p className="text-sm text-gray-400 mt-1">
                {barbershop.hora_abertura && barbershop.hora_fechamento
                  ? `${barbershop.hora_abertura} - ${barbershop.hora_fechamento}`
                  : 'Clique para configurar os horários'}
              </p>
            </div>
          </div>
        </button>
      </div>

      {/* Modais */}
      <AddressModal
        isOpen={showAddressModal}
        onClose={() => setShowAddressModal(false)}
        barbershopId={id}
        onSuccess={loadBarbershop}
      />

      <WorkingHoursModal
        isOpen={showHoursModal}
        onClose={() => setShowHoursModal(false)}
        barbershopId={id}
        onSuccess={loadBarbershop}
      />
    </div>
  );
}
